import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import styles from "./NewsLetterPage.module.css";
import { useSubscribers, useUpdateSubscription } from "../../../hooks/admin/useAdminNewsLetter";

const NewsTable = () => {
  const { subscribers, isLoading, error } = useSubscribers();
  const updateSubscription = useUpdateSubscription();

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const toggleSubscription = async (id, email, currentStatus) => {
    const newStatus = currentStatus === "subscribed" ? "unsubscribed" : "subscribed";
    await updateSubscription.mutate({ id, email, status: newStatus });
  };

  const columns = React.useMemo(() => [
    { 
      accessorKey: "email", 
      header: "EMAIL" 
    },
    { 
      accessorKey: "status", 
      header: "UPDATE",
      cell: ({ row }) => {
        const { id, email, status } = row.original;
        const isSubscribed = status === "subscribed";
        
        return (
          <button 
            className={`${styles.statusButton} ${isSubscribed ? styles.subscribed : styles.unsubscribed}`}
            onClick={() => toggleSubscription(id, email, status)}
            disabled={updateSubscription.isLoading}
          >
            {isSubscribed ? "Subscribed" : "Unsubscribed"}
          </button>
        );
      }
    },
  ], [updateSubscription.isLoading]);

  const table = useReactTable({
    data: subscribers?.data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    state: {
      pagination,
    },
  });

  if (isLoading) {
    return <div className={styles.loading}>Loading subscribers...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error loading subscribers: {error.message}</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className={styles.headerRow}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={styles.headerCell}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`${styles.bodyRow} ${
                    index % 2 === 0 ? styles.evenRow : styles.oddRow
                  }`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className={styles.bodyCell}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className={`${styles.bodyCell} ${styles.noData}`}
                >
                  No subscribers found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className={styles.paginationContainer}>
          <button
            className={`${styles.pageButton} ${
              !table.getCanPreviousPage() ? styles.disabled : ""
            }`}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <span className={styles.pageInfo}>
            Page {pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <button
            className={`${styles.pageButton} ${
              !table.getCanNextPage() ? styles.disabled : ""
            }`}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsTable;